import { createContext, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * UX-092/BR-110 (v3.26): at most one Header overlay surface — the Account
 * popover or the mobile Navigation drawer — may be open at a time. Both are
 * independent Radix primitives owned by different components (`AppShell`
 * renders the Sheet; the caller renders its own `AccountMenu` dropdown), so
 * they need a shared switch rather than two uncoordinated `open` states —
 * see the suggested `HeaderOverlay` model in the spec delta.
 *
 * `'navigation'` names the Drawer. Any other non-null string names one
 * Account trigger instance. A plain `'account'` constant would not be
 * enough: `root-layout.tsx` renders *two* `AccountMenu` instances (a
 * `compact` one for the always-visible mobile row, a full one inside the
 * `lg:` desktop `headerEnd`) that both exist in the DOM at once and are
 * merely CSS-hidden depending on viewport — a single shared `'account'`
 * value would flip both dropdowns open together, and Radix's
 * `DropdownMenuContent` portals to `document.body` regardless of its
 * trigger's own `hidden` wrapper, so the CSS-hidden instance's content would
 * still render, floating, on top of the visible layout. Each instance must
 * therefore claim a distinct key so opening one cannot also open the other
 * — only the Drawer's fixed `'navigation'` key is a suitable target for
 * "close me when *any* Account trigger opens" style checks (`!== null`).
 */
export type HeaderOverlay = string | null

interface HeaderOverlayContextValue {
  overlay: HeaderOverlay
  setOverlay: (overlay: HeaderOverlay) => void
}

const HeaderOverlayContext = createContext<HeaderOverlayContextValue | null>(null)

export function HeaderOverlayProvider({ children }: { children: ReactNode }) {
  const [overlay, setOverlay] = useState<HeaderOverlay>(null)
  const value = useMemo(() => ({ overlay, setOverlay }), [overlay])
  return <HeaderOverlayContext.Provider value={value}>{children}</HeaderOverlayContext.Provider>
}

/**
 * Falls back to independent local state outside an `AppShell` (e.g. a
 * component rendered in isolation in a unit test) rather than throwing —
 * mutual exclusion is a UX refinement, not a hard dependency.
 */
export function useHeaderOverlay(): HeaderOverlayContextValue {
  const context = useContext(HeaderOverlayContext)
  const [localOverlay, setLocalOverlay] = useState<HeaderOverlay>(null)
  if (context) {
    return context
  }
  return { overlay: localOverlay, setOverlay: setLocalOverlay }
}
